/* eslint-disable react/prop-types */
import { useState, useEffect, useRef } from "react";
import {
  CreditCard,
  Check,
  Copy,
  Radio,
  Wifi,
  Receipt,
  CheckCircle,
  CheckCircle2,
  Clock,
  AlertTriangle,
  RotateCcw,
  XCircle,
  Loader2,
  HelpCircle,
  ArrowLeft,
  ShieldCheck,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import useSettings from "@/hooks/useSettings";
import { toast } from "react-hot-toast";
import {
  markTerminalPaymentDoneApi,
  cancelTerminalPaymentApi,
} from "@/api/take-order/take-order";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

/**
 * OrderCardPaymentResponseDialog displays response details for card payments on orders.
 * Shows terminal details and interactive multi-step status check when has_pax_terminal is true,
 * or manual card confirmation details if false.
 *
 * @param {object} props
 * @param {boolean} props.isOpen
 * @param {() => void} props.onClose
 * @param {object} props.responseData - API response containing status, message, and data (uuid, amount_minor, device_id, reference, etc.)
 * @param {object} props.orderData - Active order data
 * @param {number|string} [props.paymentAmount] - Amount being charged
 * @param {Function} [props.onPaymentCompleted] - Callback after successfully marking payment done
 * @param {Function} [props.onPaymentCancelled] - Callback after successfully cancelling terminal payment
 */
export default function OrderCardPaymentResponseDialog({
  isOpen,
  onClose,
  responseData,
  orderData,
  paymentAmount,
  onPaymentCompleted,
  onPaymentCancelled,
}) {
  const { settings } = useSettings();
  const hasPaxTerminal = settings?.has_pax_terminal === true;

  // Alert duration in seconds from settings (defaults to 30)
  const defaultAlertTime =
    Number(settings?.payment_alert_time) > 0
      ? Number(settings?.payment_alert_time)
      : 30;

  // State machine: 'COUNTDOWN' | 'STATUS_CHECK' | 'CONFIRM_DONE' | 'WAIT_OR_CANCEL'
  const [step, setStep] = useState("COUNTDOWN");
  const [timeLeft, setTimeLeft] = useState(defaultAlertTime);
  const [copied, setCopied] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const timerRef = useRef(null);

  // Destructure response fields
  const status = responseData?.data?.status || responseData?.status || "dispatched";
  const deviceId =
    responseData?.data?.device_id ||
    responseData?.data?.dispatched_terminal_payments?.[0]?.device_id;
  const uuid =
    responseData?.data?.uuid ||
    responseData?.data?.dispatched_terminal_payments?.[0]?.uuid ||
    "";
  const paymentType = responseData?.data?.type || "sale";
  const currency =
    responseData?.data?.currency ||
    responseData?.data?.dispatched_terminal_payments?.[0]?.currency ||
    "GBP";
  const reference = responseData?.data?.reference || `ORDER-${orderData?.id || ""}`;
  const message =
    responseData?.message ||
    (hasPaxTerminal
      ? "Terminal payment dispatched to the device."
      : "Payment added and order completed.");

  // Calculate amount to display: prioritized by explicit payment amount requested
  let displayAmount = "0.00";
  if (paymentAmount !== undefined && paymentAmount !== null && Number(paymentAmount) > 0) {
    displayAmount = Number(paymentAmount).toFixed(2);
  } else if (responseData?.data?.amount_minor !== undefined && responseData?.data?.amount_minor !== null) {
    displayAmount = (responseData.data.amount_minor / 100).toFixed(2);
  } else if (
    responseData?.data?.dispatched_terminal_payments?.[0]?.amount_minor !== undefined &&
    responseData?.data?.dispatched_terminal_payments?.[0]?.amount_minor !== null
  ) {
    displayAmount = (responseData.data.dispatched_terminal_payments[0].amount_minor / 100).toFixed(2);
  } else if (orderData?.total_price) {
    displayAmount = Number(orderData.total_price).toFixed(2);
  }

  const currencySymbol = currency === "GBP" ? "£" : "$";

  // Reset dialog state whenever opened
  useEffect(() => {
    if (isOpen) {
      setStep("COUNTDOWN");
      setTimeLeft(defaultAlertTime);
      setIsSubmitting(false);
    } else {
      if (timerRef.current) clearInterval(timerRef.current);
    }
  }, [isOpen, defaultAlertTime]);

  // Countdown timer effect
  useEffect(() => {
    if (!isOpen || !hasPaxTerminal || step !== "COUNTDOWN") {
      if (timerRef.current) clearInterval(timerRef.current);
      return;
    }

    timerRef.current = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          setStep("STATUS_CHECK");
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [isOpen, hasPaxTerminal, step]);

  // Handle Copy to Clipboard
  const handleCopy = () => {
    if (!uuid) return;
    navigator.clipboard.writeText(uuid);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Action: Mark as Done
  const handleConfirmMarkDone = async () => {
    if (!uuid) {
      toast.error("Transaction UUID not found");
      return;
    }

    setIsSubmitting(true);
    try {
      const res = await markTerminalPaymentDoneApi(uuid);
      if (res?.status === "success") {
        toast.success(res?.message || "Terminal payment manually marked as done.");
        if (onPaymentCompleted) {
          await onPaymentCompleted(res);
        }
        onClose();
      } else {
        toast.error(res?.message || "Failed to mark payment as done.");
      }
    } catch (err) {
      console.error("Error marking terminal payment done:", err);
      toast.error("Error marking payment as done. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Action: Cancel Terminal Payment
  const handleCancelTerminalPayment = async () => {
    if (!uuid) {
      toast.error("Transaction UUID not found");
      return;
    }

    setIsSubmitting(true);
    try {
      const res = await cancelTerminalPaymentApi(uuid);
      if (res?.status === "success") {
        toast.success(res?.message || "Terminal payment cancelled.");
        if (onPaymentCancelled) {
          await onPaymentCancelled(res);
        }
        onClose();
      } else {
        toast.error(res?.message || "Failed to cancel terminal payment.");
      }
    } catch (err) {
      console.error("Error cancelling terminal payment:", err);
      toast.error("Error cancelling payment request. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Action: Wait for a while (Reset countdown and return to step 1)
  const handleWaitForAWhile = () => {
    setTimeLeft(defaultAlertTime);
    setStep("COUNTDOWN");
  };

  // Percentage calculation for progress indicator
  const progressPercent = defaultAlertTime > 0 ? (timeLeft / defaultAlertTime) * 100 : 0;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[480px] bg-cblack4 border-gray-800 text-white overflow-hidden p-6 animate-fadeIn shadow-2xl">
        
        {/* ========================================================================= */}
        {/* STEP 1: COUNTDOWN (Terminal Payment Dispatched)                            */}
        {/* ========================================================================= */}
        {step === "COUNTDOWN" && (
          <>
            <DialogHeader className="flex flex-col justify-center items-center gap-2 text-center">
              <div className="relative flex justify-center items-center h-12 w-12 rounded-full bg-cyan-500/10 text-cyan-400 border border-cyan-500/20">
                <CreditCard className="h-6 w-6 z-10" />
                {hasPaxTerminal ? (
                  <>
                    <span className="absolute inset-0 rounded-full bg-cyan-500/20 animate-ping opacity-75" />
                    <Wifi className="absolute -top-1 -right-1 h-4 w-4 text-cyan-400 animate-pulse" />
                  </>
                ) : (
                  <CheckCircle className="absolute -top-1 -right-1 h-4 w-4 text-cgreen" />
                )}
              </div>

              <DialogTitle className="text-cyan-400 text-xl font-bold tracking-tight">
                {hasPaxTerminal ? "Terminal Payment Dispatched" : "Card Payment Successful"}
              </DialogTitle>
              <DialogDescription className="text-cwhite3 text-xs max-w-[340px]">
                {message}
              </DialogDescription>
            </DialogHeader>

            <div className="flex flex-col gap-3 mt-2">
              {/* Active Countdown Progress Banner (only if PAX terminal) */}
              {hasPaxTerminal && (
                <div className="bg-cyan-950/30 border border-cyan-500/30 rounded-xl p-3 flex flex-col gap-2 relative overflow-hidden">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Clock className={`h-4 w-4 ${timeLeft <= 5 ? "text-amber-400 animate-bounce" : "text-cyan-400 animate-spin"}`} />
                      <span className="text-xs font-semibold text-cwhite2">
                        PAX Alert Countdown
                      </span>
                    </div>
                    <span
                      className={`text-xs font-mono font-bold px-2 py-0.5 rounded-full border ${
                        timeLeft <= 5
                          ? "bg-amber-500/20 border-amber-500/40 text-amber-300 animate-pulse"
                          : "bg-cyan-500/20 border-cyan-500/40 text-cyan-300"
                      }`}
                    >
                      {timeLeft}s remaining
                    </span>
                  </div>

                  {/* Progress Bar */}
                  <div className="w-full bg-gray-800 rounded-full h-2 overflow-hidden">
                    <div
                      className={`h-full transition-all duration-1000 ease-linear rounded-full ${
                        timeLeft <= 5
                          ? "bg-gradient-to-r from-amber-500 to-orange-400"
                          : "bg-gradient-to-r from-cyan-500 to-teal-400"
                      }`}
                      style={{ width: `${progressPercent}%` }}
                    />
                  </div>
                </div>
              )}

              {/* Prominent Amount Card */}
              <div className="bg-cblack3 p-3.5 rounded-xl border border-gray-800 text-center flex flex-col justify-center items-center shadow-inner relative overflow-hidden">
                <span className="text-[10px] text-cwhite3 uppercase tracking-wider font-semibold">
                  Payment Amount
                </span>
                <span className="text-cyellow text-3xl font-black font-sans mt-1 flex items-center justify-center gap-0.5">
                  <span>{currencySymbol}</span>
                  <span>{displayAmount}</span>
                </span>
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-28 h-28 bg-cyan-500/5 blur-2xl rounded-full pointer-events-none" />
              </div>

              {/* Details Grid */}
              <div className="grid grid-cols-2 gap-2.5 bg-cblack3 p-3 rounded-lg border border-gray-800/80 text-xs">
                <div className="flex flex-col gap-0.5">
                  <span className="text-cwhite3 text-[11px]">Payment Status:</span>
                  <span className="inline-flex items-center gap-1 w-fit px-2 py-0.5 rounded-full text-xs font-semibold bg-cgreen/15 text-cgreen border border-cgreen/30">
                    <span className="w-1.5 h-1.5 rounded-full bg-cgreen animate-pulse" />
                    <span className="capitalize">{status}</span>
                  </span>
                </div>

                {hasPaxTerminal && deviceId && deviceId !== "N/A" ? (
                  <div className="flex flex-col gap-0.5">
                    <span className="text-cwhite3 text-[11px]">Terminal Device:</span>
                    <span className="font-semibold text-cwhite2 flex items-center gap-1">
                      <Radio className="h-3.5 w-3.5 text-cyellow" />
                      Device #{deviceId}
                    </span>
                  </div>
                ) : (
                  <div className="flex flex-col gap-0.5">
                    <span className="text-cwhite3 text-[11px]">Payment Method:</span>
                    <span className="font-semibold text-cwhite2 capitalize">Manual Card</span>
                  </div>
                )}

                <div className="flex flex-col gap-0.5 pt-1.5 border-t border-gray-800/50">
                  <span className="text-cwhite3 text-[11px]">Order Reference:</span>
                  <span className="font-semibold text-cwhite2 flex items-center gap-1">
                    <Receipt className="h-3 w-3 text-cyan-400" />
                    {reference}
                  </span>
                </div>

                <div className="flex flex-col gap-0.5 pt-1.5 border-t border-gray-800/50">
                  <span className="text-cwhite3 text-[11px]">Transaction Type:</span>
                  <span className="font-semibold text-cwhite2 capitalize">{paymentType}</span>
                </div>
              </div>

              {/* UUID box with 1-click Copy if available */}
              {uuid && (
                <div className="flex flex-col gap-1 bg-cblack3 p-2.5 rounded-lg border border-gray-800">
                  <span className="text-cwhite3 text-[11px]">Transaction UUID:</span>
                  <div className="flex items-center gap-2 bg-cblack4 p-1.5 rounded border border-gray-800 mt-0.5">
                    <span className="font-mono text-[11px] text-cwhite2 break-all flex-1 select-all select-none">
                      {uuid}
                    </span>
                    <button
                      type="button"
                      onClick={handleCopy}
                      className="p-1 rounded hover:bg-cblack7 text-cwhite3 hover:text-white transition shrink-0"
                      title="Copy UUID"
                    >
                      {copied ? (
                        <Check className="h-3.5 w-3.5 text-cgreen animate-bounce" />
                      ) : (
                        <Copy className="h-3.5 w-3.5" />
                      )}
                    </button>
                  </div>
                </div>
              )}

              {/* Customer Instruction banner */}
              {hasPaxTerminal && deviceId && deviceId !== "N/A" && (
                <div className="flex items-center gap-2.5 p-2.5 rounded-lg border border-cyan-500/20 bg-cyan-500/5 text-xs text-cwhite3">
                  <Radio className="h-4 w-4 text-cyan-400 shrink-0 animate-pulse" />
                  <span>Please ask the customer to tap, insert, or swipe their card on Terminal #{deviceId}.</span>
                </div>
              )}
            </div>

            <DialogFooter className="mt-3 flex gap-2 sm:gap-2">
              {hasPaxTerminal && (
                <Button
                  type="button"
                  variant="outline"
                  className="flex-1 bg-cblack3 border-gray-700 hover:bg-gray-800 text-cwhite2 text-xs font-semibold py-2 rounded-lg transition"
                  onClick={() => setStep("STATUS_CHECK")}
                >
                  <HelpCircle className="h-3.5 w-3.5 mr-1.5 text-cyan-400" />
                  Check Status Now
                </Button>
              )}
              <Button
                type="button"
                className={`w-full ${hasPaxTerminal ? "flex-1" : ""} bg-cyan-500 hover:bg-cyan-600 text-white font-bold py-2 rounded-lg transition text-xs`}
                onClick={onClose}
              >
                Done
              </Button>
            </DialogFooter>
          </>
        )}

        {/* ========================================================================= */}
        {/* STEP 2: STATUS_CHECK ("Is the payment complete in pax terminal?")          */}
        {/* ========================================================================= */}
        {step === "STATUS_CHECK" && (
          <>
            <DialogHeader className="flex flex-col justify-center items-center gap-2 text-center">
              <div className="relative flex justify-center items-center h-14 w-14 rounded-full bg-amber-500/10 text-amber-400 border border-amber-500/30">
                <HelpCircle className="h-7 w-7 z-10" />
                <span className="absolute inset-0 rounded-full bg-amber-500/20 animate-ping opacity-60" />
              </div>

              <DialogTitle className="text-white text-xl font-bold tracking-tight">
                Is Payment Complete on PAX Terminal?
              </DialogTitle>
              <DialogDescription className="text-cwhite3 text-xs max-w-[360px]">
                The waiting time for the PAX terminal has elapsed. Did the customer finish the transaction on the terminal?
              </DialogDescription>
            </DialogHeader>

            <div className="flex flex-col gap-3 mt-3">
              {/* Order Summary Pill */}
              <div className="bg-cblack3 p-4 rounded-xl border border-gray-800 flex items-center justify-between">
                <div className="flex flex-col">
                  <span className="text-[11px] text-cwhite3 uppercase tracking-wider font-semibold">
                    Amount to Verify
                  </span>
                  <span className="text-cyellow text-2xl font-black font-sans">
                    {currencySymbol}{displayAmount}
                  </span>
                </div>
                <div className="text-right flex flex-col gap-0.5">
                  <span className="text-xs font-semibold text-cwhite2 flex items-center justify-end gap-1">
                    <Receipt className="h-3 w-3 text-cyan-400" />
                    {reference}
                  </span>
                  {deviceId && (
                    <span className="text-[11px] text-cwhite3 flex items-center justify-end gap-1">
                      <Radio className="h-3 w-3 text-amber-400" />
                      Terminal #{deviceId}
                    </span>
                  )}
                </div>
              </div>

              <div className="p-3 rounded-lg border border-gray-800 bg-cblack3 text-xs text-cwhite3 text-center">
                Please check the physical PAX terminal screen to confirm whether the payment was approved.
              </div>
            </div>

            <DialogFooter className="mt-4 flex flex-col sm:flex-col gap-2.5">
              <Button
                type="button"
                className="w-full bg-cgreen hover:bg-cgreen/90 text-black font-bold py-2.5 rounded-lg transition flex items-center justify-center gap-2 shadow-lg shadow-cgreen/10 text-sm"
                onClick={() => setStep("CONFIRM_DONE")}
              >
                <CheckCircle2 className="h-4 w-4 text-black" />
                Yes, mark as done
              </Button>
              <Button
                type="button"
                variant="outline"
                className="w-full bg-cblack3 border-gray-700 hover:bg-gray-800 text-cwhite2 font-semibold py-2.5 rounded-lg transition flex items-center justify-center gap-2 text-sm"
                onClick={() => setStep("WAIT_OR_CANCEL")}
              >
                <Clock className="h-4 w-4 text-amber-400" />
                No, Not done yet
              </Button>
            </DialogFooter>
          </>
        )}

        {/* ========================================================================= */}
        {/* STEP 3: CONFIRM_DONE (Confirm Terminal Payment & Mark Done)                */}
        {/* ========================================================================= */}
        {step === "CONFIRM_DONE" && (
          <>
            <DialogHeader className="flex flex-col justify-center items-center gap-2 text-center">
              <div className="relative flex justify-center items-center h-14 w-14 rounded-full bg-cgreen/10 text-cgreen border border-cgreen/30">
                <ShieldCheck className="h-7 w-7 z-10" />
                <span className="absolute inset-0 rounded-full bg-cgreen/20 animate-pulse" />
              </div>

              <DialogTitle className="text-white text-xl font-bold tracking-tight">
                Confirm Terminal Payment
              </DialogTitle>
              <DialogDescription className="text-cwhite3 text-xs max-w-[360px]">
                Confirm that the payment transaction was successfully completed on the PAX terminal.
              </DialogDescription>
            </DialogHeader>

            <div className="flex flex-col gap-3 mt-3">
              {/* Detailed Confirmation Card */}
              <div className="bg-cblack3 p-4 rounded-xl border border-gray-800 flex flex-col gap-2.5">
                <div className="flex justify-between items-center pb-2 border-b border-gray-800">
                  <span className="text-xs text-cwhite3">Amount Paid:</span>
                  <span className="text-cyellow text-lg font-bold">
                    {currencySymbol}{displayAmount}
                  </span>
                </div>
                <div className="flex justify-between items-center text-xs">
                  <span className="text-cwhite3">Order:</span>
                  <span className="text-cwhite2 font-semibold">{reference}</span>
                </div>
                {deviceId && (
                  <div className="flex justify-between items-center text-xs">
                    <span className="text-cwhite3">PAX Terminal:</span>
                    <span className="text-cwhite2 font-semibold">Device #{deviceId}</span>
                  </div>
                )}
                {uuid && (
                  <div className="flex flex-col gap-1 pt-1.5 border-t border-gray-800">
                    <span className="text-[10px] text-cwhite3">Transaction UUID:</span>
                    <span className="font-mono text-[10px] text-cwhite3 break-all bg-cblack4 p-1.5 rounded border border-gray-800">
                      {uuid}
                    </span>
                  </div>
                )}
              </div>

              <div className="p-3 rounded-lg border border-cgreen/20 bg-cgreen/5 text-xs text-cwhite3 flex items-start gap-2">
                <CheckCircle className="h-4 w-4 text-cgreen shrink-0 mt-0.5" />
                <span>
                  After ensuring everything is OK and the transaction is successfully completed in PAX terminal, mark this request as paid.
                </span>
              </div>
            </div>

            <DialogFooter className="mt-4 flex flex-col sm:flex-col gap-2.5">
              <Button
                type="button"
                disabled={isSubmitting}
                className="w-full bg-cgreen hover:bg-cgreen/90 text-black font-bold py-2.5 rounded-lg transition flex items-center justify-center gap-2 shadow-lg shadow-cgreen/10 text-sm"
                onClick={handleConfirmMarkDone}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Marking as Paid...
                  </>
                ) : (
                  <>
                    <Check className="h-4 w-4" />
                    Confirm & Mark as Done
                  </>
                )}
              </Button>
              <Button
                type="button"
                variant="ghost"
                disabled={isSubmitting}
                className="w-full text-cwhite3 hover:text-white hover:bg-gray-800 py-2 rounded-lg text-xs"
                onClick={() => setStep("STATUS_CHECK")}
              >
                <ArrowLeft className="h-3.5 w-3.5 mr-1" />
                Back
              </Button>
            </DialogFooter>
          </>
        )}

        {/* ========================================================================= */}
        {/* STEP 4: WAIT_OR_CANCEL ("Wait for a while or Cancel request")              */}
        {/* ========================================================================= */}
        {step === "WAIT_OR_CANCEL" && (
          <>
            <DialogHeader className="flex flex-col justify-center items-center gap-2 text-center">
              <div className="relative flex justify-center items-center h-14 w-14 rounded-full bg-rose-500/10 text-rose-400 border border-rose-500/30">
                <AlertTriangle className="h-7 w-7 z-10" />
                <span className="absolute inset-0 rounded-full bg-rose-500/20 animate-pulse" />
              </div>

              <DialogTitle className="text-white text-xl font-bold tracking-tight">
                Payment Still Pending
              </DialogTitle>
              <DialogDescription className="text-cwhite3 text-xs max-w-[360px]">
                Do you want to wait for some time to complete the payment in PAX terminal, or if no transaction is processing in PAX you can cancel the Terminal Payment Dispatched request.
              </DialogDescription>
            </DialogHeader>

            <div className="flex flex-col gap-3 mt-3">
              {/* Summary Card */}
              <div className="bg-cblack3 p-3.5 rounded-xl border border-gray-800 flex items-center justify-between text-xs">
                <div className="flex flex-col">
                  <span className="text-[10px] text-cwhite3 uppercase tracking-wider font-semibold">
                    Payment Amount
                  </span>
                  <span className="text-cyellow text-lg font-bold font-sans">
                    {currencySymbol}{displayAmount}
                  </span>
                </div>
                <div className="text-right flex flex-col gap-0.5">
                  <span className="font-semibold text-cwhite2">{reference}</span>
                  {deviceId && <span className="text-[11px] text-cwhite3">Terminal #{deviceId}</span>}
                </div>
              </div>

              <div className="p-3 rounded-lg border border-amber-500/20 bg-amber-500/5 text-xs text-cwhite3">
                If the customer needs more time to enter their PIN or insert card, select <strong>&quot;Yes, wait for a while&quot;</strong>. If the transaction was aborted, click <strong>&quot;Cancel Request&quot;</strong>.
              </div>
            </div>

            <DialogFooter className="mt-4 flex flex-col sm:flex-col gap-2.5">
              <Button
                type="button"
                disabled={isSubmitting}
                className="w-full bg-cyan-500 hover:bg-cyan-600 text-white font-bold py-2.5 rounded-lg transition flex items-center justify-center gap-2 text-sm"
                onClick={handleWaitForAWhile}
              >
                <RotateCcw className="h-4 w-4" />
                Yes, wait for a while
              </Button>

              <Button
                type="button"
                variant="destructive"
                disabled={isSubmitting}
                className="w-full bg-rose-600/90 hover:bg-rose-600 text-white font-bold py-2.5 rounded-lg transition flex items-center justify-center gap-2 text-sm border border-rose-500/40"
                onClick={handleCancelTerminalPayment}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Cancelling Request...
                  </>
                ) : (
                  <>
                    <XCircle className="h-4 w-4" />
                    No Transaction Processing, Cancel Request
                  </>
                )}
              </Button>

              <Button
                type="button"
                variant="ghost"
                disabled={isSubmitting}
                className="w-full text-cwhite3 hover:text-white hover:bg-gray-800 py-2 rounded-lg text-xs"
                onClick={() => setStep("STATUS_CHECK")}
              >
                <ArrowLeft className="h-3.5 w-3.5 mr-1" />
                Back
              </Button>
            </DialogFooter>
          </>
        )}

      </DialogContent>
    </Dialog>
  );
}

